import type {
	Cluster,
	ClusterDriveMinutes,
	ClusterDriveMinutesResult,
	ClusterOption,
	CreateClusterBody,
	CreateDriverBody,
	CreateTransportPlaceBody,
	CreateTransportProviderBody,
	CreateTransportRequestBody,
	CreateVehicleBody,
	Driver,
	DriverWeek,
	ListDriversQueryInput,
	ListVehiclesQueryInput,
	Paginated,
	PatchClusterBody,
	PatchDriverBody,
	PatchTransportPlaceBody,
	PatchTransportProviderBody,
	PatchTransportSettingsBody,
	PatchVehicleBody,
	SetDropoffsBody,
	SetGroupFacilitiesBody,
	TransportCreatedTrip,
	TransportDropoffsResult,
	TransportNocTripRow,
	TransportPlace,
	TransportPlaceHotelOption,
	TransportPlaceOption,
	TransportProvider,
	TransportProviderOption,
	TransportProviderTripRow,
	TransportRequestsPage,
	TransportSettings,
	UserGroupDetail,
	Vehicle,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { queryClient } from "./query-client";
import { type TransportRequestsFilter, invalidateTransport, transportKeys } from "./transport";

/**
 * M10 fleet, places, clusters and settings (`/transport/vehicles`,
 * `/transport/drivers`, `/transport/providers`, `/clusters`,
 * `/transport/places`, `/transport/dropoffs`, `/transport/settings`), the
 * user-group facility rows, and the NOC's "Request a trip". Trip reads and
 * the other trip writes are S4's (`lib/api/transport.ts`).
 *
 * Keys: the fleet under `["transport-fleet", …]`; trips are keyed under
 * `["transport", …]` — a trip write invalidates both (`invalidateTrips`).
 */

export interface FleetListQuery {
	page: number;
	page_size: number;
	search?: string;
	sort?: string;
	dir?: "asc" | "desc";
}

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

export const fleetKeys = {
	all: ["transport-fleet"] as const,
	vehicles: (q: ListVehiclesQueryInput) => ["transport-fleet", "vehicles", clean(q)] as const,
	drivers: (q: ListDriversQueryInput) => ["transport-fleet", "drivers", clean(q)] as const,
	driverWeek: (id: string, start: string | undefined) => ["transport-fleet", "driver-week", id, start ?? null] as const,
	providers: (q: FleetListQuery) => ["transport-fleet", "providers", clean(q)] as const,
	providerOptions: ["transport-fleet", "providers", "options"] as const,
	vehicleOptions: (clusterId: string) => ["transport-fleet", "vehicles", "options", clusterId] as const,
	clusters: ["transport-fleet", "clusters"] as const,
	clusterOptions: ["transport-fleet", "clusters", "options"] as const,
	driveMinutes: ["transport-fleet", "drive-minutes"] as const,
	places: ["transport-fleet", "places"] as const,
	placeOptions: (gamesId: string) => ["transport-fleet", "place-options", gamesId] as const,
	placeHotelOptions: ["transport-fleet", "places", "hotel-options"] as const,
	dropoffs: (gamesId: string) => ["transport-fleet", "dropoffs", gamesId] as const,
	settings: (gamesId: string) => ["transport-fleet", "settings", gamesId] as const,
};

export const invalidateFleet = () => queryClient.invalidateQueries({ queryKey: fleetKeys.all });

/** A trip changed: the trip reads (`["transport", …]`) and the fleet's trip counts / driver weeks. */
export const invalidateTrips = () => Promise.all([invalidateTransport(), invalidateFleet()]);

// ── Vehicles ────────────────────────────────────────────────────────────────

/** `GET /transport/vehicles` — one offset page, `count(*) OVER()` and the trips-today LATERAL in the same statement. */
export const vehiclesListOptions = (q: ListVehiclesQueryInput) =>
	queryOptions({
		queryKey: fleetKeys.vehicles(q),
		queryFn: ({ signal }) => api.get<Paginated<Vehicle>>("/transport/vehicles", { query: clean({ ...q }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** The usual-vehicles picker in the driver sheet: one cluster's vehicles (tens), one capped page. */
export const clusterVehiclesOptions = (clusterId: string) =>
	queryOptions({
		queryKey: fleetKeys.vehicleOptions(clusterId),
		queryFn: ({ signal }) =>
			api.get<Paginated<Vehicle>>("/transport/vehicles", { query: { cluster_id: clusterId, page: 1, page_size: 100, sort: "plate", dir: "asc" }, signal }),
		staleTime: 30_000,
	});

export const createVehicle = (body: CreateVehicleBody) => api.post<Vehicle>("/transport/vehicles", body);

/** `version` read with the row → 409 STALE_VERSION when someone saved first. */
export const updateVehicle = (id: string, body: PatchVehicleBody) => api.patch<Vehicle>(`/transport/vehicles/${id}`, body);

// ── Drivers ─────────────────────────────────────────────────────────────────

export const driversListOptions = (q: ListDriversQueryInput) =>
	queryOptions({
		queryKey: fleetKeys.drivers(q),
		queryFn: ({ signal }) => api.get<Paginated<Driver>>("/transport/drivers", { query: clean({ ...q }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

export const createDriver = (body: CreateDriverBody) => api.post<Driver>("/transport/drivers", body);

export const updateDriver = (id: string, body: PatchDriverBody) => api.patch<Driver>(`/transport/drivers/${id}`, body);

/** `GET /transport/drivers/:id/week?start=` — gist `(driver_id, busy)` ∩ the week, ≤ 500 rows, one statement. `start` omitted = this week. */
export const driverWeekOptions = (id: string, start: string | undefined) =>
	queryOptions({
		queryKey: fleetKeys.driverWeek(id, start),
		queryFn: ({ signal }) => api.get<DriverWeek>(`/transport/drivers/${id}/week`, { query: clean({ start }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

// ── Providers (SUPER_ADMIN writes) ──────────────────────────────────────────

export const providersListOptions = (q: FleetListQuery) =>
	queryOptions({
		queryKey: fleetKeys.providers(q),
		queryFn: ({ signal }) => api.get<Paginated<TransportProvider>>("/transport/providers", { query: clean({ ...q }), signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** Active companies for a picker (vehicle / driver sheets, the group facility editor): tens of rows, cached. */
export const providerOptionsOptions = () =>
	queryOptions({
		queryKey: fleetKeys.providerOptions,
		queryFn: ({ signal }) => api.get<TransportProviderOption[]>("/transport/providers/options", { signal }),
		staleTime: 5 * 60_000,
	});

export const createProvider = (body: CreateTransportProviderBody) => api.post<TransportProvider>("/transport/providers", body);

export const updateProvider = (id: string, body: PatchTransportProviderBody) => api.patch<TransportProvider>(`/transport/providers/${id}`, body);

// ── Clusters & drive minutes ────────────────────────────────────────────────

/** `GET /clusters` — tens of rows with their usage counts (vehicles, drivers, venues, entry points, places). */
export const clustersListOptions = () =>
	queryOptions({
		queryKey: fleetKeys.clusters,
		queryFn: ({ signal }) => api.get<Paginated<Cluster>>("/clusters", { query: { page: 1, page_size: 100, sort: "sort_order", dir: "asc" }, signal }),
		staleTime: 60_000,
	});

/** `GET /clusters/options` — any signed-in GMS role (pickers on venues, entry points, the fleet sheets). */
export const clusterOptionsOptions = () =>
	queryOptions({
		queryKey: fleetKeys.clusterOptions,
		queryFn: ({ signal }) => api.get<ClusterOption[]>("/clusters/options", { signal }),
		staleTime: 10 * 60_000,
	});

export const createCluster = (body: CreateClusterBody) => api.post<Cluster>("/clusters", body);

export const updateCluster = (id: string, body: PatchClusterBody) => api.patch<Cluster>(`/clusters/${id}`, body);

/** 409 CONFLICT while a vehicle, driver, venue, entry point or place still uses it. */
export const deleteCluster = (id: string) => api.delete<null>(`/clusters/${id}`);

/** `GET /clusters/drive-minutes` — the whole matrix (≤ n² rows for tens of clusters). */
export const driveMinutesOptions = () =>
	queryOptions({
		queryKey: fleetKeys.driveMinutes,
		queryFn: ({ signal }) => api.get<ClusterDriveMinutesResult>("/clusters/drive-minutes", { signal }),
		staleTime: 60_000,
	});

/** Replace-set: one DELETE + one INSERT … SELECT. The rows sent are the matrix kept. */
export const putDriveMinutes = (rows: readonly ClusterDriveMinutes[]) => api.put<ClusterDriveMinutesResult>("/clusters/drive-minutes", { rows });

// ── Places ──────────────────────────────────────────────────────────────────

export const placesListOptions = () =>
	queryOptions({
		queryKey: fleetKeys.places,
		queryFn: ({ signal }) => api.get<Paginated<TransportPlace>>("/transport/places", { query: { page: 1, page_size: 100, sort: "name", dir: "asc" }, signal }),
		staleTime: 60_000,
	});

/**
 * `GET /transport/places/options?games_id=` — every place a trip may use:
 * venues with a cluster, entry points, active transport places; `own_dropoff`
 * marks the caller's contingent's drop-off. Read by the drop-off sheet and
 * the Request-a-trip sheet.
 */
export const placeOptionsOptions = (gamesId: string) =>
	queryOptions({
		queryKey: fleetKeys.placeOptions(gamesId),
		queryFn: ({ signal }) => api.get<TransportPlaceOption[]>("/transport/places/options", { query: { games_id: gamesId }, signal }),
		staleTime: 5 * 60_000,
	});

/** `GET /transport/places/hotel-options` — the place form's Hotel picker: every lodging building (≤ 200) with the place linked to it. */
export const placeHotelOptionsOptions = () =>
	queryOptions({
		queryKey: fleetKeys.placeHotelOptions,
		queryFn: ({ signal }) => api.get<TransportPlaceHotelOption[]>("/transport/places/hotel-options", { signal }),
		staleTime: 30_000,
	});

export const createPlace = (body: CreateTransportPlaceBody) => api.post<TransportPlace>("/transport/places", body);

export const updatePlace = (id: string, body: PatchTransportPlaceBody) => api.patch<TransportPlace>(`/transport/places/${id}`, body);

/** 409 CONFLICT while a trip starts or ends there (`trip_count`): deactivate it instead. */
export const deletePlace = (id: string) => api.delete<null>(`/transport/places/${id}`);

// ── Contingent drop-offs & settings (per games) ─────────────────────────────

export const dropoffsOptions = (gamesId: string) =>
	queryOptions({
		queryKey: fleetKeys.dropoffs(gamesId),
		queryFn: ({ signal }) => api.get<TransportDropoffsResult>("/transport/dropoffs", { query: { games_id: gamesId }, signal }),
		staleTime: 60_000,
	});

/** Replace-set for the games: one DELETE + one INSERT … SELECT. */
export const putDropoffs = (body: SetDropoffsBody) => api.put<TransportDropoffsResult>("/transport/dropoffs", body);

export const transportSettingsOptions = (gamesId: string) =>
	queryOptions({
		queryKey: fleetKeys.settings(gamesId),
		queryFn: ({ signal }) => api.get<TransportSettings>(`/transport/settings/${gamesId}`, { signal }),
		staleTime: 60_000,
	});

export const patchTransportSettings = (gamesId: string, body: PatchTransportSettingsBody) => api.patch<TransportSettings>(`/transport/settings/${gamesId}`, body);

// ── User-group facilities (SUPER_ADMIN) ─────────────────────────────────────

/** Replace-all: the companies sent are the companies kept. A non-facility group → 422 GROUP_ROLE_MISMATCH. */
export const setUserGroupFacilities = (groupId: string, body: SetGroupFacilitiesBody) => api.put<UserGroupDetail>(`/user-groups/${groupId}/facilities`, body);

// ── Request a trip (NOC view; the desk may reuse the sheet) ─────────────────
// Trip reads, cancel and Start / Arrived are S4's (`lib/api/transport.ts`).

/** `POST /transport/requests` — an ADHOC venue trip. ORGANIZATION logins are held to `noc_lead_hours` (422 TRIP_LEAD_TIME, `details.min_pickup_at`). */
export const createTrip = (body: CreateTransportRequestBody) => api.post<TransportCreatedTrip>("/transport/requests", body);

// ── The NOC's and the provider's trip lists ────────────────────────────────
// Same endpoint and filters as the desk (`transportRequestsOptions`); the API
// picks the row shape by who asks (`view`). Typed per view, each under its
// own key suffix so a narrowed page never shares a cache entry with another.

export type NocTripsPage = Omit<TransportRequestsPage, "items"> & { items: TransportNocTripRow[] };
export type ProviderTripsPage = Omit<TransportRequestsPage, "items"> & { items: TransportProviderTripRow[] };

/** `GET /transport/requests` as a NOC: its own passengers and counts only (PLAN §0b Q3, built in SQL). */
export const nocTripsOptions = (f: TransportRequestsFilter) =>
	queryOptions({
		queryKey: [...transportKeys.requests(f), "noc"] as const,
		queryFn: async ({ signal }): Promise<NocTripsPage> => {
			const page = await api.get<TransportRequestsPage>("/transport/requests", { query: clean({ ...f }), signal });
			return { ...page, items: page.items.filter((r): r is TransportNocTripRow => r.view === "noc") };
		},
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/** `GET /transport/requests?tab=day&date=` as a provider: every trip of the day on its vehicles. Polled while visible (it presses Start / Arrived from here). */
export const providerTripsOptions = (f: TransportRequestsFilter) =>
	queryOptions({
		queryKey: [...transportKeys.requests(f), "provider"] as const,
		queryFn: async ({ signal }): Promise<ProviderTripsPage> => {
			const page = await api.get<TransportRequestsPage>("/transport/requests", { query: clean({ ...f }), signal });
			return { ...page, items: page.items.filter((r): r is TransportProviderTripRow => r.view === "provider") };
		},
		placeholderData: keepPreviousData,
		staleTime: 15_000,
		refetchInterval: 60_000,
		refetchIntervalInBackground: false,
	});
